import type { CanvasMetadata, ColumnModel, SchemaModel, TableModel } from '@bits2schema/shared';
import type { Command as CanvasCommand } from '../../canvas/commands';
import { createDeleteNoteCommand } from '../../canvas/commands';
import { useCommandStack } from '../../command/stack';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useT } from '../../i18n';
import type { SchemaCommand } from '../commands';
import { createDeleteColumnCommand } from '../commands';
import { findColumnDependents } from '../mutations';

interface DeleteColumnDialogProps {
  table: TableModel;
  column: ColumnModel;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  schemaExecute: (command: SchemaCommand) => void;
  canvasExecute: (command: CanvasCommand) => void;
  onClose: () => void;
}

/**
 * Potvrzení smazání sloupce (E4-US6) — blokuje, pokud je sloupec součástí
 * FK (zdrojové i cílové role, AC), jinak jen varuje, pokud jde o jediný
 * sloupec primárního klíče (AC — "vyžaduje explicitní potvrzení", ne blok).
 */
export function DeleteColumnDialog({ table, column, schema, canvas, schemaExecute, canvasExecute, onClose }: DeleteColumnDialogProps) {
  const t = useT();
  const { runGroup } = useCommandStack();
  const dependents = findColumnDependents(schema, table.id, column.id);
  const blocked = dependents.length > 0;
  const isLastPrimaryKeyColumn =
    table.primaryKey?.columns.length === 1 && table.primaryKey.columns[0] === column.name;
  const attachedNotes = canvas.notes.filter(
    (note) => note.attachment.type === 'column' && note.attachment.id === column.id,
  );

  const confirm = () => {
    runGroup({ key: 'schema.command.deleteColumn', params: { name: column.name } }, () => {
      const originalIndex = table.columns.findIndex((candidate) => candidate.id === column.id);
      schemaExecute(
        createDeleteColumnCommand(
          table.id,
          column,
          originalIndex,
          table.primaryKey,
          table.uniqueConstraints,
          table.indexes,
        ),
      );
      for (const note of attachedNotes) {
        canvasExecute(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)));
      }
    });
    onClose();
  };

  return (
    <ConfirmDialog
      title={t('inspector.column.deleteConfirmTitle', { name: column.name })}
      confirmLabel={t('common.delete')}
      danger
      confirmDisabled={blocked}
      onCancel={onClose}
      onConfirm={confirm}
      message={
        blocked ? (
          <p className="notice notice--error">
            {t('inspector.column.deleteBlocked', { name: dependents[0]?.foreignKey.name ?? '' })}
          </p>
        ) : (
          <>
            {isLastPrimaryKeyColumn && <p className="notice notice--error">{t('inspector.column.deletePkWarning')}</p>}
            {attachedNotes.length > 0 && (
              <p className="hint">{t('inspector.table.deleteConfirmWarningNotes', { count: attachedNotes.length })}</p>
            )}
          </>
        )
      }
    />
  );
}
